import React, { useState, useEffect } from 'react';
import { useConsent } from './ConsentProvider';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Settings, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import CookiePreferencesModal from './CookiePreferencesModal';
import {
  saveLocalConsent,
  createConsentPayload,
  initializeTrackers,
  getOrCreateAnonymousId
} from './consentManager';
import { base44 } from '@/api/base44Client';

export default function CookieBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const { hasConsent, isLoading } = useConsent();
  const [user, setUser] = useState(null);

  useEffect(() => {
    loadUser();
  }, []);

  useEffect(() => {
    if (isLoading) return;
    setIsVisible(!hasConsent);
  }, [hasConsent, isLoading]);

  const loadUser = async () => {
    try {
      const userData = await base44.auth.me();
      setUser(userData);
    } catch (error) {
      // User not logged in
      setUser(null);
    }
  };

  const handleAcceptAll = async () => {
    const preferences = {
      analytics: true,
      marketing: true
    };
    
    await saveConsent(preferences);
    setIsVisible(false);
    initializeTrackers();
  };

  const handleRejectAll = async () => {
    const preferences = {
      analytics: false,
      marketing: false
    };
    
    await saveConsent(preferences);
    setIsVisible(false);
  };

  const handleCustomize = () => {
    setShowPreferences(true);
  };

  const handleSavePreferences = async (preferences) => {
    await saveConsent(preferences);
    setShowPreferences(false);
    setIsVisible(false);
    initializeTrackers();
  };

  const saveConsent = async (preferences) => {
    // Sauvegarder localement
    saveLocalConsent(preferences);
    
    // Sauvegarder en DB si utilisateur connecté
    if (user?.id) {
      try {
        const payload = createConsentPayload(preferences, user.id);
        await base44.entities.CookieConsent.create(payload);
      } catch (error) {
        console.error('Error saving consent to DB:', error);
        // Continue même si l'enregistrement DB échoue
      }
    } else {
      // Utilisateur non connecté, enregistrer quand même avec anonymous_id
      try {
        const payload = createConsentPayload(preferences, null);
        await base44.entities.CookieConsent.create(payload);
      } catch (error) {
        console.error('Error saving anonymous consent:', error);
      }
    }
  };

  return (
    <>
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
          >
            <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-2xl border border-slate-200 p-6 md:p-8">
              <div className="flex items-start gap-4">
                <div className="bg-amber-100 p-3 rounded-xl flex-shrink-0">
                  <Cookie className="w-6 h-6 text-amber-600" />
                </div>
                
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    Nous respectons votre vie privée
                  </h3>
                  <p className="text-slate-600 text-sm md:text-base mb-4">
                    TrésoPilot utilise des cookies pour assurer le bon fonctionnement du service et améliorer votre 
                    expérience. Certains cookies nécessitent votre consentement. Vous pouvez accepter tous les cookies, 
                    les refuser, ou personnaliser vos préférences.
                  </p>
                  <p className="text-slate-500 text-xs mb-4">
                    En savoir plus : <a href="/cookies" className="text-emerald-600 hover:text-emerald-700 underline">Politique Cookies</a>
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={handleAcceptAll}
                      className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 flex-1 sm:flex-initial"
                    >
                      Tout accepter
                    </Button>
                    <Button
                      onClick={handleRejectAll}
                      variant="outline"
                      className="flex-1 sm:flex-initial border-slate-300"
                    >
                      Tout refuser
                    </Button>
                    <Button
                      onClick={handleCustomize}
                      variant="outline"
                      className="flex-1 sm:flex-initial border-slate-300"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Personnaliser
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <CookiePreferencesModal
        isOpen={showPreferences}
        onClose={() => setShowPreferences(false)}
        onSave={handleSavePreferences}
      />
    </>
  );
}